import * as pdfjsLib from 'pdfjs-dist/webpack';
import { PDFDocumentProxy, PDFProgressData } from 'pdfjs-dist/webpack';
import React, { useEffect, useState } from 'react';
import { useInViewEffect } from 'react-hook-inview';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import * as api from '../api/api';
import pdfLogo from '../assets/img/pdf-tool/logo@2x.png';
import * as selectors from '../dff/dff-selectors';
import { loadUser, mountSelector } from '../dff/dff-slice';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { FilesFilterComponent } from '../pdf-tool-manage/common/files-filter/files-filter-component';
import {
  fetchMounts,
  fetchPdfs,
  resetState,
  selectPdfs,
  setFileName,
} from '../pdf-tool-manage/pdf-slice';
import { useAppDispatch } from '../store/store';
import { Toolbar } from '../toolbar/toolbar-component';
import { MainMenuComponent } from '../menu/main-menu';

require('./pdf-tool-split.scss');

export interface PdfToolStateProps {
  mount: api.Mount;
  user: api.User;
}

export interface PdfToolDispatchProps {
  selectMount: (mount: api.Mount) => void;
  loadUser: (user: api.User) => void;
}

export interface PdfToolOwnProps {}

export interface PdfToolProps
  extends PdfToolStateProps,
    PdfToolDispatchProps,
    PdfToolOwnProps {
  history: any;
}

const PdfFirstPage = ({ file, history }) => {
  const [inView, setInView] = useState(false);
  const [finishedRendering, setFinishedRendering] = useState(false);

  const mount = useSelector(mountSelector);

  const dispatch = useAppDispatch();

  const handleFileClick = async (e, f: any) => {
    e.preventDefault();
    history.push(
      `/pdf-tool/split/${f.mountId}?path=${encodeURIComponent(f.path)}`,
    );
    dispatch(setFileName(f.file.name.split('.pdf')[0]));
  };

  const fileNameTruncated = (name: string) => {
    if (name.length > 15) {
      return name.substring(0, 15) + '...';
    }
    return name;
  };

  const cancelLoading = (task: PDFDocumentProxy) => {
    task.destroy();
  };

  const ref = useInViewEffect(
    ([entry], observer) => {
      // if (entry.isIntersecting) {
      //   observer.unobserve(entry.target);
      // }
      setInView(entry.isIntersecting);
    },
    { threshold: 0.5 },
  );

  useEffect(() => {
    let isSubscribed = true;
    let loadingTask: any;
    if (inView && !finishedRendering) {
      const url = `/content/api/v2/mounts/${
        file.mountId
      }/files/get?path=${encodeURIComponent(file.path)}`;
      if (isSubscribed) {
        loadingTask = pdfjsLib.getDocument(url);
        loadingTask.onProgress = function (data: PDFProgressData) {
          if (!isSubscribed) {
            cancelLoading(loadingTask);
            return;
          }
        };
        const canvas = document.getElementById(
          file.file.name,
        ) as HTMLCanvasElement;
        if (canvas === null) {
          return;
        }

        loadingTask.promise.then((pdf: PDFDocumentProxy) => {
          const firstPagePromise = pdf.getPage(1);

          firstPagePromise.then(
            (page) => {
              const viewport = page.getViewport({ scale: 1 });
              const context = canvas.getContext('2d');
              canvas.height = viewport.height;
              canvas.width = viewport.width;
              // Render PDF page into canvas context
              context.clearRect(0, 0, canvas.width, canvas.height);
              context.beginPath();
              const renderContext = {
                canvasContext: context,
                viewport: viewport,
              };
              page.render(renderContext).promise.then(
                () => {
                  setFinishedRendering(true);
                },
                (e) => {
                  setFinishedRendering(true);
                },
              );
            },
            (e) => {},
          );
        }, (e) => {
          console.error(e);
          setFinishedRendering(true);
        });
      }
    } else {
      if (loadingTask) {
        cancelLoading(loadingTask);
      }
    }

    return () => {
      isSubscribed = false;
    };
  }, [inView, mount]);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <div
      className="pdf-tool-split__result"
      ref={ref}
      id={`pdf-file-div-${file.file.name}`}
    >
      <div
        onClick={(e) => handleFileClick(e, file)}
        className="pdf-tool-split__result-link"
      >
        <div className="pdf-tool-split__thumbnail-container">
          <div
            className="pdf-tool-split__thumbnail-image"
            style={{ height: '100%' }}
          >
            <canvas
              id={file.file.name}
              style={{ maxWidth: '100%', maxHeight: '100%' }}
            ></canvas>
          </div>
          <a className="pdf-tool-split__split-button" href="#">
            Split
          </a>
        </div>
        <div className="pdf-tool-split__name" data-tip={file.file.name}>
          {fileNameTruncated(file.file.name)}
        </div>
      </div>
    </div>
  );
};

export const PdfToolSplitComponent: React.FC<PdfToolProps> = ({ history }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMounts, setIsLoadingMounts] = useState(true);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);

  const user = useSelector(selectors.user);
  const filteredFiles = useSelector(selectPdfs);
  const mount = useSelector(mountSelector);

  const dispatch = useAppDispatch();

  useEffect(() => {
    document.title = 'Koofr EXP - PDF Split';
  }, []);

  useEffect(() => {
    fetch('/api/v2/user/appconfig')
      .then((results) => {
        if (results.ok) {
          return results.json();
        }
        throw new Error(results.status as any);
      })
      .then((data) => {
        dispatch(loadUser(data));
        setIsLoading(false);
      })
      .then(() => {
        dispatch(fetchMounts({ loadRecent: true })).then(() => {
          setIsLoadingMounts(false);
        });
      })
      .then(() => {
        dispatch(fetchPdfs({ mountId: mount?.id || 'recent', query: '' })).then(
          () => {
            setIsLoadingFiles(false);
          },
        );
      })
      .catch((err) => {
        console.error(err);
        setIsLoadingMounts(false);
        setIsLoading(false);
        setIsLoadingFiles(false);
      });
    dispatch(resetState());
  }, []);

  return (
    <div className="pdf-tool-split">
      <Toolbar>
        <div className='l-toolbar__column'>
          <Link to="/">
            <img src={pdfLogo} alt="Koofr PDF Tool logo" className="l-toolbar__logo"/>
          </Link>
          <div className='divider'></div>
          <div className='app-name'><span className="text-normal">PDF Tool &gt;</span> Split PDF</div>
        </div>
        <div className='l-toolbar__column stretch'></div>
        <div className='l-toolbar__column'>
          <Link to="/pdf-tool/merge/" className='gray-link'>Merge PDF</Link>
          <div className='divider'></div>
          <MainMenuComponent/>
        </div>
      </Toolbar>
      <div className="pdf-tool-split-container">
        {!isLoading && !user ? (
          <a
            href={`/login?redirect=${encodeURIComponent('/pdf-tool/')}`}
            className="login-button"
          >
            Sign in with Koofr
          </a>
        ) : null}

        {user && user.extra.subscription.isPayingUser ? (
          <FilesFilterComponent></FilesFilterComponent>
        ) : null}

        <div
          className="pdf-tool-split__docs-container"
          id="docsContainer"
        ></div>

        {filteredFiles.length ? (
          <div className="pdf-tool-split__results-container">
            <div className="pdf-tool-split__intro">
              <div className="pdf-tool-split__text">
                Select PDF file you wish to split into multiple files or extract
                pages from it into separate PDF file.
              </div>
            </div>
            {filteredFiles.map((f, index) => (
              <PdfFirstPage
                key={`page ${index} ${f.file.name}`}
                file={f}
                history={history}
              />
            ))}
          </div>
        ) : null}

        {!isLoading && user && !user.extra.subscription.isPayingUser ? (
          <div className="upgrade">
            <p className="upgrade__text">
              Koofr PDF Tool is only available to paying users.
            </p>
            <a
              href="https://app.koofr.net/app/account"
              className="login-button"
            >
              Upgrade your account
            </a>
          </div>
        ) : null}

        {isLoadingMounts || isLoadingFiles ? <LoadingSpinner /> : null}
      </div>
    </div>
  );
};
